import isNil from "lodash/isNil";
import { createSearchParams } from "react-router-dom";
import { formatDateAsPerServer, isValidDate } from "../../../utils";

export interface ProfileListFilters {
  fromMonitoring?: boolean;
  page?: number | string;
  statuses?: string | string[];
  userNumbers?: string | (number | string)[];
  riskRating?: string | string[];
  reviewBeforeDate?: string;
  reviewAfterDate?: string;
  searchTerm?: string;
  sortType?: string;
}

export interface InitialProfileFilters {
  queryPageNumber: number;
  queryStatus: string[];
  queryUserNumber: (number | string)[];
  queryRiskRating: string[];
  queryReviewBeforeDate: string;
  queryReviewAfterDate: string;
  querySearchTerm: string;
  querySortType: string;
}

export interface CurrentProfileFilters {
  status: string[];
  userNumber: (number | string)[];
  riskRating: string[];
  reviewBeforeDate: string;
  reviewAfterDate: string;
  page: number;
  searchTerm: string;
  sortType: string;
}

export interface FilterOverrides {
  status?: string[];
  userNumber?: (number | string)[];
  riskRating?: string[];
  reviewBeforeDate?: string;
  reviewAfterDate?: string;
  page?: number;
  searchTerm?: string;
  sortType?: string;
}

const addParam = (
  params: URLSearchParams,
  key: string,
  value: unknown
): void => {
  if (!isNil(value) && value !== "") {
    params.set(key, value as string);
  }
};

const addArrayOrStringParam = (
  params: URLSearchParams,
  key: string,
  value: unknown
): void => {
  if (Array.isArray(value) && value.length > 0) {
    params.set(key, value.join(","));
  } else if (typeof value === "string" && value.length > 0) {
    params.set(key, value);
  }
};

const getFormattedDate = (date: string): string | undefined => {
  if (isValidDate(date)) {
    const formatted = formatDateAsPerServer(new Date(date));
    return formatted ?? undefined;
  }
  return undefined;
};

export const buildProfilesBackUrl = (
  filters: ProfileListFilters | null | undefined = {}
): string => {
  const safeFilters = filters ?? {};
  const {
    fromMonitoring,
    page,
    statuses,
    userNumbers,
    riskRating,
    reviewBeforeDate,
    reviewAfterDate,
    searchTerm,
    sortType,
  } = safeFilters;

  if (fromMonitoring) {
    return "/protect/alerts/alerts-dashboard";
  }

  const baseUrl = "/protect";
  const params = new URLSearchParams();

  addParam(params, "page", page);

  addArrayOrStringParam(params, "status", statuses);
  addArrayOrStringParam(params, "assignee", userNumbers);
  addArrayOrStringParam(params, "riskRating", riskRating);

  if (reviewBeforeDate) {
    const formatted = getFormattedDate(reviewBeforeDate);
    addParam(params, "beforeDate", formatted ?? reviewBeforeDate);
  }
  if (reviewAfterDate) {
    const formatted = getFormattedDate(reviewAfterDate);
    addParam(params, "afterDate", formatted ?? reviewAfterDate);
  }

  addParam(params, "search", searchTerm);

  addParam(params, "sortType", sortType);

  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

export const getInitialFiltersFromUrl = (
  searchParams: URLSearchParams,
  defaultSortType = "lastModified"
): InitialProfileFilters => {
  const queryPageNumber = Number.parseInt(searchParams.get("page") ?? "1", 10);
  const queryStatus =
    searchParams.get("status")?.split(",").filter(Boolean) ?? [];
  const queryUserNumber =
    searchParams.get("assignee")?.split(",").filter(Boolean) ?? [];
  const queryRiskRating =
    searchParams.get("riskRating")?.split(",").filter(Boolean) ?? [];
  const queryReviewBeforeDate = searchParams.get("beforeDate") ?? "";
  const queryReviewAfterDate = searchParams.get("afterDate") ?? "";
  const querySearchTerm = searchParams.get("search") ?? "";
  const querySortType = searchParams.get("sortType") ?? defaultSortType;

  return {
    queryPageNumber,
    queryStatus,
    queryUserNumber,
    queryRiskRating,
    queryReviewBeforeDate,
    queryReviewAfterDate,
    querySearchTerm,
    querySortType,
  };
};

const mergeArrayFilter = (
  override: string[] | (number | string)[] | undefined,
  current: string[] | (number | string)[]
): string[] | (number | string)[] => override ?? current;

export const buildFilteredUrl = (
  currentFilters: CurrentProfileFilters,
  overrideFilters: Partial<FilterOverrides> = {}
): string => {
  const filters = {
    status: mergeArrayFilter(
      overrideFilters.status,
      currentFilters.status
    ) as string[],
    userNumber: mergeArrayFilter(
      overrideFilters.userNumber,
      currentFilters.userNumber
    ),
    riskRating: mergeArrayFilter(
      overrideFilters.riskRating,
      currentFilters.riskRating
    ) as string[],
    reviewBeforeDate:
      overrideFilters.reviewBeforeDate ?? currentFilters.reviewBeforeDate,
    reviewAfterDate:
      overrideFilters.reviewAfterDate ?? currentFilters.reviewAfterDate,
    page: overrideFilters.page ?? currentFilters.page,
    searchTerm: overrideFilters.searchTerm ?? currentFilters.searchTerm,
    sortType: overrideFilters.sortType ?? currentFilters.sortType,
  };

  const paramsObject: Record<string, string> = {
    page: filters.page.toString(),
  };

  const addIfPresent = (
    key: string,
    value: string[] | (number | string)[] | string
  ): void => {
    if (Array.isArray(value) && value.length > 0) {
      paramsObject[key] = value.join(",");
    } else if (typeof value === "string" && value) {
      paramsObject[key] = value;
    }
  };

  addIfPresent("status", filters.status);
  addIfPresent("assignee", filters.userNumber);
  addIfPresent("riskRating", filters.riskRating);

  if (filters.reviewBeforeDate) {
    const formatted = getFormattedDate(filters.reviewBeforeDate);
    paramsObject.beforeDate = formatted ?? filters.reviewBeforeDate;
  }
  if (filters.reviewAfterDate) {
    const formatted = getFormattedDate(filters.reviewAfterDate);
    paramsObject.afterDate = formatted ?? filters.reviewAfterDate;
  }

  addIfPresent("search", filters.searchTerm);

  paramsObject.sortType = filters.sortType;

  const searchParams = createSearchParams(paramsObject);
  return `/protect?${searchParams.toString()}`;
};
